import React from 'react';
import { useFormikContext, FormikProps, FormikValues } from 'formik';
import fp from 'lodash/fp';
import { Organization as OrganizationType, AcademicSession } from 'types/schema-generated-types';

export type FieldValue = { value?: number; label: string };
export type CoEducatorFieldValue = {
  firstName: string;
  lastName: string;
  email: string;
  id: number;
};
const getCurrentOrg = (
  id: number,
  organizations?: Array<OrganizationType>
): OrganizationType | undefined => {
  if (id && organizations) {
    return organizations.find(organization => id === organization.id);
  }
};

const useFormValues = (
  organizations: Array<OrganizationType> = []
): [
  OrganizationType | undefined,
  Array<FieldValue>,
  Array<CoEducatorFieldValue>,
  Array<FieldValue>
] => {
  const { values }: FormikProps<FormikValues> = useFormikContext();

  const organizationField = values.organization || values.CREATE_CLASS?.organization;
  const educatorField = values.educator || values.CREATE_CLASS?.educator;
  const coeducatorField = values.coeducators || values.CREATE_CLASS?.coeducators;

  const currentOrg = getCurrentOrg(+organizationField, organizations);

  const primaryEducatorFieldValues = React.useMemo(
    () =>
      currentOrg && currentOrg.users
        ? currentOrg.users
            .map(({ user = {} }) => ({
              label: `${user.firstName} ${user.lastName} (${user.username})`,
              value: user.id
            }))
            .filter(({ value: primaryId }) => {
              const found =
                coeducatorField && coeducatorField?.find(({ id }: any) => id === primaryId);
              return !found;
            })
        : [],
    [currentOrg, coeducatorField]
  );
  const coeducatorFieldValues = React.useMemo(
    () =>
      currentOrg && currentOrg.users
        ? currentOrg.users
            .map(({ user }) => {
              return {
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.username,
                id: user.id
              };
            })
            .filter(({ id }) => {
              const found = id === +educatorField;
              const foundCoeducator =
                coeducatorField &&
                coeducatorField?.find(({ id: coeducatorId }: any) => coeducatorId === id);
              return !found && !foundCoeducator;
            })
        : [],
    [currentOrg, coeducatorField, educatorField]
  );

  const schoolYearFieldValues = React.useMemo(() => {
    if (!currentOrg?.academicSessions) return [];

    return fp.flow(
      fp.map((session: AcademicSession) => ({ label: session.name, value: session.id })),
      fp.orderBy('label', 'asc')
    )(currentOrg.academicSessions as AcademicSession[]) as FieldValue[];
  }, [currentOrg]);

  return [currentOrg, primaryEducatorFieldValues, coeducatorFieldValues, schoolYearFieldValues];
};

export default useFormValues;
